/**
 * @file ClientOptionSecurity.js
 * @description This file contains the ClientOptionSecurity component, which is a reusable form for displaying and editing an client's security information.
 */

import { useState, useEffect } from 'react';
import { Dropdown } from '../helpers/Dropdown';
import { SearchableDropdown } from '../helpers/SearchableDropdown';
import {
    getClientOptions,
    createClientOption,
    updateClientOption,
    deleteClientOption,
} from '../../services/clientService';
import Tooltip from '../ui/tooltips/Tooltip';
import Toast from '../ui/toasts/Toast';
import {
    SecurityAttributes,
    ClientOptionEnum,
    IdentifierEnum,
} from '../../utils/data/enums';
import { useAuthContext } from '../../store/Contexts';
import { getText } from '../../i18n';

const ClientOptionSecurity = ({
    clientRecord = null,
    isSelfRegistrationEnabled = false,
}) => {
    const [isSelfRegistrationEnabledState, setIsSelfRegistrationEnabledState] =
        useState(false);
    const [initialRegistrationOption, setInitialRegistrationOption] = useState(
        {},
    );
    const [initialPasswordMinimumOption, setInitialPasswordMinimumOption] =
        useState({});
    const [initialPasswordMaximumOption, setInitialPasswordMaximumOption] =
        useState({});
    const [initialPasswordHistoryOption, setInitialPasswordHistoryOption] =
        useState({});
    const [
        initialRepeatedValuesInPasswordOption,
        setInitialRepeatedValuesInPasswordOption,
    ] = useState({});

    const registrationOptions = initialRegistrationOption?.selectList || [];
    const passwordMinimumLength =
        initialPasswordMinimumOption?.selectList || [];
    const passwordMaximumLength =
        initialPasswordMaximumOption?.selectList || [];
    const passwordHistoryCount = initialPasswordHistoryOption?.selectList || [];

    const [selectedRegistrationIds, setSelectedRegistrationIds] = useState([]);
    const [selectedPasswordMinimumLength, setSelectedPasswordMinimumLength] =
        useState();

    const [selectedPasswordMaximumLength, setSelectedPasswordMaximumLength] =
        useState();
    const [selectedPasswordHistoryCount, setSelectedPasswordHistoryCount] =
        useState();
    const [
        selectedRepeatedValuesInPassword,
        setSelectedRepeatedValuesInPassword,
    ] = useState();

    const mappedRegistrationOptions = registrationOptions.map(opt => ({
        name: opt.label,
        id: opt.id,
    }));

    const [isOpen, setIsOpen] = useState(true);
    const { hasAttribute } = useAuthContext();

    const getOptions = async () => {
        try {
            const selfData = await getClientOptions(clientRecord?.id || 0, [
                ClientOptionEnum.EnableUserSelfRegistration,
            ]);

            const selfObj = (selfData || []).find(
                item =>
                    item.clientOptionId ===
                        ClientOptionEnum.EnableUserSelfRegistration ||
                    item.id === ClientOptionEnum.EnableUserSelfRegistration,
            );
            const selfEnabled = selfObj ? selfObj.value === 'true' : false;

            setIsSelfRegistrationEnabledState(selfEnabled);

            const registrationEnum = selfEnabled
                ? ClientOptionEnum.SelfRegistrationIdentifiers
                : ClientOptionEnum.RegistrationIdentifiers;

            const data = await getClientOptions(clientRecord?.id || 0, [
                registrationEnum,
                ClientOptionEnum.PasswordMinimumLength,
                ClientOptionEnum.PasswordMaximumLength,
                ClientOptionEnum.PasswordHistoryCount,
                ClientOptionEnum.PasswordAllowDuplicateValues,
            ]);

            const registrationOptionName = selfEnabled
                ? 'Self Registration Identifiers'
                : 'Registration Identifiers';

            const registrationObject = data.find(
                item => item.name === registrationOptionName,
            );
            if (registrationObject) {
                setInitialRegistrationOption(registrationObject);
                setSelectedRegistrationIds(registrationObject.values);
            } else {
                Toast.error(
                    `There was an error attempting to get the ${registrationOptionName.toLowerCase()} option.`,
                );
            }

            const passwordMinimumObject = data.find(
                item => item.name === 'Password Minimum Length',
            );
            if (passwordMinimumObject) {
                setInitialPasswordMinimumOption(passwordMinimumObject);
                setSelectedPasswordMinimumLength(passwordMinimumObject.value);
            } else {
                Toast.error(
                    getText('msg_8b9c927a1c8f'),
                );
            }

            const passwordMaximumObject = data.find(
                item => item.name === 'Password Maximum Length',
            );
            if (passwordMaximumObject) {
                setInitialPasswordMaximumOption(passwordMaximumObject);
                setSelectedPasswordMaximumLength(passwordMaximumObject.value);
            } else {
                Toast.error(
                    getText('msg_7c26ffd8a4c0'),
                );
            }

            const passwordHistoryObject = data.find(
                item => item.name === 'Password History Count',
            );
            if (passwordHistoryObject) {
                setInitialPasswordHistoryOption(passwordHistoryObject);
                setSelectedPasswordHistoryCount(passwordHistoryObject.value);
            } else {
                Toast.error(
                    getText('msg_37596b6bf47b'),
                );
            }

            const repeatedValuesInPasswordObject = data.find(
                item =>
                    item.name ===
                    'Allow Consecutive/Duplicate Values in Password',
            );
            if (repeatedValuesInPasswordObject) {
                setInitialRepeatedValuesInPasswordOption(
                    repeatedValuesInPasswordObject,
                );
                setSelectedRepeatedValuesInPassword(
                    repeatedValuesInPasswordObject.value,
                );
            } else {
                Toast.error(
                    getText('msg_197587b8a758'),
                );
            }
        } catch (error) {
            console.error('Could not fetch:', error);
        }
    };

    useEffect(() => {
        getOptions();
    }, [clientRecord?.id]);

    const handleRegistrationChange = selectedOptions => {
        const ids = selectedOptions.map(opt => opt.id);
        setSelectedRegistrationIds(ids);
    };

    const handlePasswordMinimumLengthChange = selectedValue => {
        const minValue = parseInt(selectedValue);
        const maxValue = parseInt(selectedPasswordMaximumLength);

        setSelectedPasswordMinimumLength(selectedValue);
        if (selectedPasswordMaximumLength && minValue > maxValue) {
            setSelectedPasswordMaximumLength(selectedValue);
        }
    };

    const handlePasswordMaximumLengthChange = selectedValue => {
        const maxValue = parseInt(selectedValue);
        const minValue = parseInt(selectedPasswordMinimumLength);

        setSelectedPasswordMaximumLength(selectedValue);
        if (selectedPasswordMinimumLength && maxValue < minValue) {
            setSelectedPasswordMinimumLength(selectedValue);
        }
    };

    const toggleAccordion = () => {
        setIsOpen(!isOpen);
    };

    const areArraysEqual = (arr1, arr2) => {
        if (arr1.length !== arr2.length) return false;
        for (let i = 0; i < arr1.length; i++) {
            if (arr1[i] !== arr2[i]) return false;
        }
        return true;
    };

    const handleSave = async () => {
        let noChanges = true;
        if (
            !isSelfRegistrationEnabledState &&
            selectedRegistrationIds.length < 3
        ) {
            return Toast.error(
                getText('msg_e6cd744015ce'),
            );
        } else if (
            parseInt(selectedPasswordMinimumLength) >
            parseInt(selectedPasswordMaximumLength)
        ) {
            return Toast.error(
                getText('msg_381bf97aee5a'),
            );
        }

        if (
            isSelfRegistrationEnabledState &&
            selectedRegistrationIds.length === 0
        ) {
            if (!initialRegistrationOption.usingDefaultValue) {
                noChanges = false;
                try {
                    await deleteClientOption(
                        clientRecord?.id || 0,
                        initialRegistrationOption.clientOptionId,
                    );
                    getOptions();
                } catch {
                    return Toast.error(
                        getText('msg_5e4874de1eb9'),
                    );
                }
            }
        } else if (initialRegistrationOption.usingDefaultValue) {
            noChanges = false;
            const option = {
                clientOptionId: initialRegistrationOption.clientOptionId,
                values: selectedRegistrationIds,
            };
            const response = await createClientOption(
                option,
                clientRecord?.id || 0,
            );
            if (response.error)
                return Toast.error(
                    getText('msg_6a5affed31ba'),
                );
            getOptions();
        } else if (
            !areArraysEqual(
                initialRegistrationOption.values,
                selectedRegistrationIds,
            )
        ) {
            noChanges = false;
            const option = {
                clientOptionId: initialRegistrationOption.clientOptionId,
                values: selectedRegistrationIds,
            };
            const response = await updateClientOption(
                option,
                clientRecord?.id || 0,
            );
            if (response.error)
                return Toast.error(
                    getText('msg_b943265f7ea2'),
                );
            getOptions();
        }

        if (initialPasswordMinimumOption.usingDefaultValue) {
            noChanges = false;
            const option = {
                clientOptionId: initialPasswordMinimumOption.clientOptionId,
                value: `${selectedPasswordMinimumLength}`,
            };
            const response = await createClientOption(
                option,
                clientRecord?.id || 0,
            );
            if (response.error)
                return Toast.error(
                    getText('msg_afc61d66b48c'),
                );
            setInitialPasswordMinimumOption(response.data);
        } else if (
            initialPasswordMinimumOption.value != selectedPasswordMinimumLength
        ) {
            noChanges = false;
            const option = {
                clientOptionId: initialPasswordMinimumOption.clientOptionId,
                value: `${selectedPasswordMinimumLength}`,
            };
            const response = await updateClientOption(
                option,
                clientRecord?.id || 0,
            );
            if (response.error)
                return Toast.error(
                    getText('msg_599e9ee9da8e'),
                );
            setInitialPasswordMinimumOption(response.data);
        }

        if (initialPasswordMaximumOption.usingDefaultValue) {
            noChanges = false;
            const option = {
                clientOptionId: initialPasswordMaximumOption.clientOptionId,
                value: `${selectedPasswordMaximumLength}`,
            };
            const response = await createClientOption(
                option,
                clientRecord?.id || 0,
            );
            if (response.error)
                return Toast.error(
                    getText('msg_e9273fa4a3ea'),
                );
            setInitialPasswordMaximumOption(response.data);
        } else if (
            initialPasswordMaximumOption.value != selectedPasswordMaximumLength
        ) {
            noChanges = false;
            const option = {
                clientOptionId: initialPasswordMaximumOption.clientOptionId,
                value: `${selectedPasswordMaximumLength}`,
            };
            const response = await updateClientOption(
                option,
                clientRecord?.id || 0,
            );
            if (response.error)
                return Toast.error(
                    getText('msg_28bf3620a7f4'),
                );
            setInitialPasswordMaximumOption(response.data);
        }

        if (initialPasswordHistoryOption.usingDefaultValue) {
            noChanges = false;
            const option = {
                clientOptionId: initialPasswordHistoryOption.clientOptionId,
                value: `${selectedPasswordHistoryCount}`,
            };
            const response = await createClientOption(
                option,
                clientRecord?.id || 0,
            );
            if (response.error)
                return Toast.error(
                    getText('msg_25942a11c838'),
                );
            setInitialPasswordHistoryOption(response.data);
        } else if (
            initialPasswordHistoryOption.value != selectedPasswordHistoryCount
        ) {
            noChanges = false;
            const option = {
                clientOptionId: initialPasswordHistoryOption.clientOptionId,
                value: `${selectedPasswordHistoryCount}`,
            };
            const response = await updateClientOption(
                option,
                clientRecord?.id || 0,
            );
            if (response.error)
                return Toast.error(
                    getText('msg_d501f8365846'),
                );
            setInitialPasswordHistoryOption(response.data);
        }

        if (initialRepeatedValuesInPasswordOption.usingDefaultValue) {
            noChanges = false;
            const option = {
                clientOptionId:
                    initialRepeatedValuesInPasswordOption.clientOptionId,
                value: `${selectedRepeatedValuesInPassword}`,
            };
            const response = await createClientOption(
                option,
                clientRecord?.id || 0,
            );
            if (response.error)
                return Toast.error(
                    getText('msg_4af84e197da7'),
                );
            setInitialRepeatedValuesInPasswordOption(response.data);
        } else if (
            initialRepeatedValuesInPasswordOption.value !=
            `${selectedRepeatedValuesInPassword}`
        ) {
            noChanges = false;
            const option = {
                clientOptionId:
                    initialRepeatedValuesInPasswordOption.clientOptionId,
                value: `${selectedRepeatedValuesInPassword}`,
            };
            const response = await updateClientOption(
                option,
                clientRecord?.id || 0,
            );
            if (response.error)
                return Toast.error(
                    getText('msg_70e46e238a0f'),
                );
            setInitialRepeatedValuesInPasswordOption(response.data);
        }

        if (noChanges) Toast.warning(getText('msg_13c3a7ac4e47'));
        else Toast.success(getText('msg_ce6cf511cb4d'));
    };

    const handleCancel = () => {
        setSelectedRegistrationIds(initialRegistrationOption.values);
        setSelectedPasswordMinimumLength(initialPasswordMinimumOption.value);
        setSelectedPasswordMaximumLength(initialPasswordMaximumOption.value);
        setSelectedPasswordHistoryCount(initialPasswordHistoryOption.value);
        setSelectedRepeatedValuesInPassword(
            initialRepeatedValuesInPasswordOption.value,
        );
    };

    const consecutiveDuplicateValuesTooltipHtml = `Our system password requirements are in place when enabled. If disabled, user passwords will need to meet this criteria:<br/>
• Password can not contain 4 consecutive repeating characters (e.g. 1111, aaaa)<br/>
• Password can not contain 4 sequential characters (e.g. 1234, abcd)<br/>
• Password can not contain 3 repeating patterns (e.g., ababab)`;

    const registrationLabel = isSelfRegistrationEnabledState
        ? 'Self Registration Identifiers'
        : 'Registration Identifiers';

    return (
        <div
            style={{
                overflow: 'hidden',
                borderRadius: '12px',
                border: 'solid 1px rgba(47, 43, 67, 0.2)',
                marginTop: '10px',
                boxShadow:
                    'rgba(47, 43, 67, 0.1) 0px -1px 0px 0px inset, rgba(47, 43, 67, 0.1) 0px 1px 3px 0px',
            }}
        >
            <div
                style={{
                    overflowY: 'auto',
                    padding: '16px',
                    resize: 'none',
                    boxShadow:
                        'rgba(47, 43, 67, 0.1) 0px -1px 0px 0px inset, rgba(47, 43, 67, 0.1) 0px 1px 3px 0px;',
                }}
            >
                <div>
                    <div style={{ marginLeft: '12px' }}>
                        {getText('msg_f25ce1b8a399')}{' '}
                        <i
                            style={{
                                color: 'var(--iNGAGED-Neutral-Color3)',
                                display: 'flex',
                                float: 'inline-end',
                                cursor: 'pointer',
                            }}
                            className={
                                'bi bi-chevron-' + (isOpen ? 'up' : 'down')
                            }
                            onClick={() => toggleAccordion()}
                        />
                    </div>
                </div>
                {isOpen && (
                    <div
                        style={{
                            marginLeft: '48px',
                            marginRight: '48px',
                            marginTop: '12px',
                        }}
                    >
                        <div>
                            {registrationLabel}{' '}
                            <span className="text-danger">*</span>
                        </div>
                        <div
                            style={{
                                color: 'var(--iNGAGED-Neutral-Color2)',
                                marginBottom: '12px',
                            }}
                        >
                            {getText('msg_c464844cff1c')}
                        </div>
                        <SearchableDropdown
                            placeholderLabel={getText('msg_8598222918d3')}
                            options={mappedRegistrationOptions}
                            optionIDField="id"
                            savedOptions={selectedRegistrationIds
                                .map(id =>
                                    mappedRegistrationOptions.find(
                                        opt => opt.id === id,
                                    ),
                                )
                                .filter(Boolean)}
                            savedOptionIDField="id"
                            updateOptions={selectedOptions =>
                                handleRegistrationChange(selectedOptions)
                            }
                            disabled={false}
                            showTags={true}
                            arrangeTags={true}
                            lockedOptionIds={
                                isSelfRegistrationEnabledState
                                    ? [
                                          IdentifierEnum.FirstName,
                                          IdentifierEnum.LastName,
                                          IdentifierEnum.UniqueID,
                                          IdentifierEnum.EmailAddress,
                                          IdentifierEnum.MobilePhoneNumber,
                                      ]
                                    : []
                            }
                        />
                        <div style={{ marginTop: '12px' }}>
                            {getText('msg_8eafc8b4725d')}
                        </div>
                        <div style={{ color: 'var(--iNGAGED-Neutral-Color2)' }}>
                            {getText('msg_c4574eb04338')}
                        </div>

                        <div style={{ marginLeft: '24px', marginTop: '12px' }}>
                            <div
                                style={{
                                    display: 'flex',
                                    gap: '5px',
                                    alignItems: 'center',
                                    marginBottom: '8px',
                                }}
                            >
                                <div style={{ minWidth: '220px' }}>
                                    {getText('msg_1e95eca8d256')}
                                </div>
                                <div style={{ width: '100px' }}>
                                    <Dropdown
                                        defaultValue={
                                            selectedPasswordMinimumLength
                                        }
                                        options={passwordMinimumLength}
                                        onChange={selectedOptions =>
                                            handlePasswordMinimumLengthChange(
                                                selectedOptions,
                                            )
                                        }
                                        useOnChangeValueFallback={true}
                                    />
                                </div>
                                <div>{getText('msg_cffa50a32cb1')}</div>
                                <div style={{ width: '100px' }}>
                                    <Dropdown
                                        defaultValue={
                                            selectedPasswordMaximumLength
                                        }
                                        options={passwordMaximumLength}
                                        onChange={selectedOptions =>
                                            handlePasswordMaximumLengthChange(
                                                selectedOptions,
                                            )
                                        }
                                        useOnChangeValueFallback={true}
                                    />
                                </div>
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    gap: '5px',
                                    alignItems: 'center',
                                }}
                            >
                                <div>{getText('msg_6357f3e6c101')}</div>
                                <div style={{ width: '100px' }}>
                                    <Dropdown
                                        defaultValue={
                                            selectedPasswordHistoryCount
                                        }
                                        options={passwordHistoryCount}
                                        onChange={selectedOptions =>
                                            setSelectedPasswordHistoryCount(
                                                selectedOptions,
                                            )
                                        }
                                        useOnChangeValueFallback={true}
                                    />
                                </div>
                                <div>{getText('msg_e58a4011508b')}</div>
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    gap: '5px',
                                    alignItems: 'center',
                                    marginTop: '8px',
                                }}
                            >
                                <div className={`form-switch`}>
                                    <input
                                        onClick={() =>
                                            setSelectedRepeatedValuesInPassword(
                                                selectedRepeatedValuesInPassword ===
                                                    'true'
                                                    ? 'false'
                                                    : 'true',
                                            )
                                        }
                                        className="form-check-input custom-switch-input"
                                        type="checkbox"
                                        checked={
                                            selectedRepeatedValuesInPassword ===
                                            'true'
                                        }
                                    />
                                </div>
                                <div>{getText('msg_25465fa6026b')}</div>
                                <Tooltip
                                    html={consecutiveDuplicateValuesTooltipHtml}
                                    placement="right"
                                >
                                    <i
                                        className="bi bi-info-circle"
                                        style={{
                                            paddingLeft: '5px',
                                            fontSize: '13px',
                                            color: '#1366ff',
                                        }}
                                    ></i>
                                </Tooltip>
                            </div>
                        </div>
                        {hasAttribute(
                            SecurityAttributes.ClientOptionsFullAccess,
                        ) && (
                            <div
                                className={`d-flex justify-content-evenly mt-5`}
                            >
                                <button
                                    type="button"
                                    onClick={handleCancel}
                                    className="ing-popup-blue-negative"
                                >
                                    {getText('msg_77dfd2135f4d')}
                                </button>
                                <button
                                    type="button"
                                    className="ing-button-primary-none"
                                    onClick={handleSave}
                                >
                                    {getText('msg_efc007a393f6')}
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ClientOptionSecurity;
